import { CreativeOSFeatures, CreativeOSPages } from '@/_shared/subscriptionPlans'
import AccessControl from '@/components/AccessControl'
import { UploadAdImage } from '@/components/FCA'
import { Stack } from '@/components/custom-components'
import { LockTagSmall } from '@/components/custom-components/LockTag'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/shadcn/breadcrumb.tsx'
import { Button } from '@/components/shadcn/button.tsx'
import { Form } from '@/components/shadcn/form.tsx'
import { useElementSize } from '@/hooks/useElementSize'
import showToastNotification from '@/hooks/useShowToast'
import { featureFlagKeys } from '@/utils/data/featureFlags'
import { trpc } from '@/utils/trpc'
import { zodResolver } from '@hookform/resolvers/zod'
import { CheckCircle, Plus } from '@mynaui/icons-react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import axios from 'axios'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { useEffect, useMemo, useState } from 'react'
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar'
import { useForm } from 'react-hook-form'
import SemiCircleProgressBar from 'react-progressbar-semicircle'
import { z } from 'zod'
import 'react-circular-progressbar/dist/styles.css'
import { cn } from '@/lib/utils'
import { Loader } from 'lucide-react'

type SearchParams = {
  sideBarOpen?: boolean
}

type AdCreativeAnalysisIndividualField = {
  strengths: string[]
  recommendations: string[]
  score: number
}

type AdCreativeAnalysis = {
  strengths: string[]
  recommendations: string[]
  scores: {
    message_clarity: number
    design_execution: number
    persuasiveness: number
    funnel_fit: number
    predicted_performance: number
  }
  totalScore: number
  letterGrade: string
  taskId: string
  individualFields: {
    first_impression: AdCreativeAnalysisIndividualField
    messaging_and_copy: AdCreativeAnalysisIndividualField
    value_proposition: AdCreativeAnalysisIndividualField
    visual_hierarchy: AdCreativeAnalysisIndividualField
    narrative_flow: AdCreativeAnalysisIndividualField | null
    psychological_triggers: AdCreativeAnalysisIndividualField
    audience_fit: AdCreativeAnalysisIndividualField
    performance_prediction: AdCreativeAnalysisIndividualField
  }
  assetUrl: string
  assetType: string
}

type AnalysisStep = {
  name: string
  completed: boolean
  inProgress: boolean
}

export const Route = createFileRoute('/feeds/creative-toolkit/creative-analysis/')({
  component: All,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const sideBarOpen = search?.sideBarOpen as boolean

    return {
      sideBarOpen,
    }
  },
})

function All() {
  const [squareRef, { width = 0 }] = useElementSize()

  const [uploadStep, setUploadStep] = useState<number>(1)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [adImageUrl, setAdImageUrl] = useState<string | undefined>(undefined)
  const [adVideoUrl, setAdVideoUrl] = useState<string | undefined>(undefined)
  const [assetType, setAssetType] = useState<'image' | 'video' | undefined>(undefined)
  const [creativeAnalysis, setCreativeAnalysis] = useState<AdCreativeAnalysis | undefined>(undefined)

  // Loading state management
  const [analysisProgress, setAnalysisProgress] = useState<number>(0)
  const [analysisSteps, setAnalysisSteps] = useState<AnalysisStep[]>([
    { name: 'Hook', completed: false, inProgress: false },
    { name: 'Messaging', completed: false, inProgress: false },
    { name: 'Value Proposition', completed: false, inProgress: false },
    { name: 'Visual Design', completed: false, inProgress: false },
    { name: 'Narrative', completed: false, inProgress: false },
    { name: 'Emotion', completed: false, inProgress: false },
    { name: 'Funnel Fit', completed: false, inProgress: false },
    { name: 'Performance', completed: false, inProgress: false },
  ])

  const creativeAnalysisFeatureFlagEnabled = useFeatureFlagEnabled(featureFlagKeys.creativeAnalysis)

  const navigate = useNavigate()

  useEffect(() => {
    if (creativeAnalysisFeatureFlagEnabled === false) {
      navigate({ to: '/feeds/creative-toolkit' })
      return
    }
  }, [creativeAnalysisFeatureFlagEnabled])

  const utils = trpc.useUtils()

  const { mutate: createCreativeAnalysis, isPending: isCreatingCreativeAnalysis } =
    trpc.createCreativeAnalysis.useMutation({
      onSuccess: (data) => {
        if ('taskId' in data) {
          setCreativeAnalysis(data)
          showToastNotification('success', {
            message: 'Creative analysis generated successfully',
          })
          utils.getUserLimits.invalidate()
          setUploadStep(2)
        }
      },
      onError: () => {
        showToastNotification('error', {
          message: 'Error',
          description: 'An error occurred. Please try again.',
        })
      },
    })

  // Simulate 20-second loading animation
  useEffect(() => {
    if (isCreatingCreativeAnalysis) {
      setAnalysisProgress(0)
      const totalDuration = assetType === 'video' ? 45000 : 30000 // 45 seconds for video, 30 seconds for image
      const progressInterval = 100 // Update progress every 100ms
      const progressIncrement = (progressInterval / totalDuration) * 100

      let currentProgress = 0

      const progressTimer = setInterval(() => {
        currentProgress += progressIncrement
        // Keep at 98% if we've exceeded total duration but analysis is still running
        const displayProgress =
          currentProgress >= 100 && isCreatingCreativeAnalysis ? 98 : Math.min(currentProgress, 98)
        setAnalysisProgress(displayProgress)

        // Update steps based on progress
        const stepProgress = (displayProgress / 100) * analysisSteps.length
        const newSteps = [...analysisSteps]

        for (let i = 0; i < newSteps.length; i++) {
          if (i < Math.floor(stepProgress)) {
            newSteps[i] = { ...newSteps[i], completed: true, inProgress: false }
          } else if (i === Math.floor(stepProgress)) {
            newSteps[i] = { ...newSteps[i], completed: false, inProgress: true }
          } else {
            newSteps[i] = { ...newSteps[i], completed: false, inProgress: false }
          }
        }

        setAnalysisSteps(newSteps)

        if (currentProgress >= 100 && !isCreatingCreativeAnalysis) {
          clearInterval(progressTimer)
        }
      }, progressInterval)

      return () => clearInterval(progressTimer)
    }
  }, [isCreatingCreativeAnalysis])

  const formSchema = z.object({
    adAsset: z.instanceof(File).optional(),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      adAsset: undefined,
    },
  })

  const handleSubmitUploadAdImage = async (values: z.infer<typeof formSchema>) => {
    setAnalysisProgress(0)
    setIsUploading(true)
    try {
      if (!values.adAsset) {
        throw new Error('Ad asset is required')
      }

      const isVideo = values.adAsset.type.startsWith('video/')
      const formData = new FormData()

      if (isVideo) {
        formData.append('videos', values.adAsset)

        const videoUploadResponse = await axios.post(
          `${import.meta.env.VITE_API_URL}creative-analyst/video-upload`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        )

        if (videoUploadResponse && videoUploadResponse.data.videoUrls?.[0]) {
          const uploadedUrl = videoUploadResponse.data.videoUrls[0].videoUrl
          setAdVideoUrl(uploadedUrl)
          setAdImageUrl(undefined)
          setAssetType('video')
          setIsUploading(false)
          createCreativeAnalysis({
            video_url: uploadedUrl,
          })
        }
      } else {
        formData.append('images', values.adAsset)

        const imageUploadResponse = await axios.post(
          `${import.meta.env.VITE_API_URL}funnel-congruence/image-upload`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        )

        if (imageUploadResponse && imageUploadResponse.data.imageUrls?.[0]) {
          const uploadedUrl = imageUploadResponse.data.imageUrls[0]
          setAdImageUrl(uploadedUrl)
          setAdVideoUrl(undefined)
          setAssetType('image')
          setIsUploading(false)
          createCreativeAnalysis({
            image_url: uploadedUrl,
          })
        }
      }
    } catch (e) {
      console.error('Error', e)
      setIsUploading(false)
    }
  }

  const adAsset = form.watch('adAsset')

  const fileSize = form.watch('adAsset')
    ? `${(form.watch('adAsset')!.size / (1024 * 1024)).toFixed(2)} MB ${form.watch('adAsset')!.type}`
    : 'file'

  const imageUrl = useMemo(() => {
    if (!adAsset) return null
    return URL.createObjectURL(adAsset)
  }, [adAsset])

  return (
    <AccessControl page={CreativeOSPages.CREATIVE_TOOLKIT_CA}>
      <Stack ref={squareRef} className="gap-3 lg:gap-6 pb-10">
        <Stack className="gap-3 lg:gap-8">
          <Stack className="gap-3 lg:gap-6">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={'/feeds/creative-toolkit'}>Toolkit</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>Ad Creative Analyzer</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <div className={'flex justify-between gap-5 items-center'}>
              <div className={''}>
                <h1 className={'text-lg lg:text-2xl font-semibold'}>Upload Your Marketing Assets</h1>
                <p className={'text-lg lg:text-base text-thememutedforeground mt-2'}>
                  Upload your ad image to analyse their congruence{' '}
                </p>
              </div>
            </div>
          </Stack>
        </Stack>
        <div className="flex flex-col gap-3">
          <Form {...form}>
            {uploadStep === 1 ? (
              <div>
                <form onSubmit={form.handleSubmit(handleSubmitUploadAdImage)}>
                  <div className="">
                    <div className="space-y-3">
                      <h3>Upload Ad Image</h3>
                      <div className="h-[50svh] lg:h-[60svh] w-full rounded-lg relative">
                        {isUploading || isCreatingCreativeAnalysis ? (
                          <div
                            style={{
                              background: `linear-gradient(to right, rgba(0,0,0,0.3), rgba(0,0,0,0.3)), 
                              url(${imageUrl})`,
                              backgroundSize: 'contain',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat',
                            }}
                            className="absolute top-0 left-0 w-full h-full flex justify-center items-center flex-col p-5 gap-8"
                          >
                            <div>
                              <CircularProgressbar
                                styles={buildStyles({
                                  pathColor: '#0CDB71',
                                  trailColor: '#CBD5E1',
                                  textColor: '#CBD5E1',
                                })}
                                className="w-[5rem] md:w-[8rem]"
                                strokeWidth={4}
                                value={analysisProgress}
                                maxValue={100}
                                text={`${Math.round(analysisProgress)}%`}
                              />
                              <p className="text-sm text-slate-100 text-center mt-3">
                                {isUploading ? 'Uploading...' : 'Analyzing...'}
                              </p>
                            </div>
                            <div className="rounded-xl px-4 py-3 bg-slate-700 bg-opacity-50 text-center lg:w-1/3">
                              <p className="text-sm text-slate-100">
                                A {fileSize} typically takes around a minute. Larger files may take longer.
                              </p>
                            </div>
                          </div>
                        ) : (
                          <UploadAdImage
                            onFileSelect={(file) => {
                              form.setValue('adAsset', file)
                            }}
                            previewUrl={assetType === 'video' ? adVideoUrl : adImageUrl}
                            disabled={isUploading}
                            onRemove={() => {
                              setAdImageUrl(undefined)
                              setAdVideoUrl(undefined)
                              setAssetType(undefined)
                              form.setValue('adAsset', undefined)
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    {isCreatingCreativeAnalysis ? (
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        {analysisSteps.map((step) => (
                          <div
                            key={step.name}
                            className={cn(
                              'rounded-md border flex justify-center items-center gap-2 p-3 transition-all duration-300',
                              {
                                'border-green-200 bg-green-50': step.completed,
                                'border-blue-200 bg-blue-50 animate-pulse': step.inProgress,
                                'border-gray-200': !step.completed && !step.inProgress,
                              },
                            )}
                          >
                            <span
                              className={`text-sm font-medium transition-colors duration-300 ${
                                step.completed ? 'text-green-500' : step.inProgress ? 'text-blue-700' : 'text-[#0F172A]'
                              }`}
                            >
                              {step.name}
                            </span>
                            <span className="transition-all duration-300">
                              {step.completed ? (
                                <CheckCircle color="#0CDB71" size={16} />
                              ) : step.inProgress ? (
                                <Loader size={16} className=" animate-spin" />
                              ) : (
                                <div className="w-4 h-4 border-2 border-gray-300 rounded-full" />
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <AccessControl
                        feature={CreativeOSFeatures.USE_CREATIVE_TOOLKIT}
                        FeatureUnlocked={
                          <div className="flex justify-end gap-3">
                            <Button
                              type="button"
                              onClick={() => {
                                form.handleSubmit(handleSubmitUploadAdImage)()
                              }}
                              disabled={Boolean(!form.watch('adAsset') || isUploading)}
                            >
                              {isUploading ? 'Capturing...' : 'Next'}
                            </Button>
                          </div>
                        }
                        FeatureLocked={
                          <div className="mt-4 flex justify-end gap-3">
                            <Button disabled={true} size={'sm'}>
                              Analyze
                              <div>{<LockTagSmall />}</div>
                            </Button>
                          </div>
                        }
                      />
                    )}
                  </div>
                </form>
              </div>
            ) : uploadStep === 2 && creativeAnalysis ? (
              <div>
                <div className="flex justify-end">
                  <div>
                    <Button
                      variant={'destructivePurple'}
                      size={'sm'}
                      onClick={() => {
                        setAssetType(undefined)
                        setAdImageUrl(undefined)
                        setAdVideoUrl(undefined)
                        setUploadStep(1)
                        setCreativeAnalysis(undefined)
                      }}
                    >
                      <Plus /> New Analysis
                    </Button>
                  </div>
                </div>
                <div className="border rounded">
                  <div className="px-6 pt-6 pb-5 border-b">
                    <h5 className="font-semibold text-lg">Overall Analysis</h5>
                  </div>

                  <div className="flex flex-col justify-center items-center">
                    <div className={'py-6 relative'}>
                      <SemiCircleProgressBar
                        strokeWidth={6}
                        percentage={creativeAnalysis.totalScore}
                        diameter={width > 750 ? 300 : undefined}
                        stroke={
                          creativeAnalysis.totalScore >= 80
                            ? '#28BE53BE'
                            : creativeAnalysis.totalScore > 40
                              ? '#FF8731'
                              : '#FF0000'
                        }
                      />
                      <div className={'absolute bottom-5 left-1/2 transform -translate-x-1/2 text-center'}>
                        <h4 className={'text-4xl font-bold mb-2'}>{creativeAnalysis.totalScore}</h4>
                        <h3 className={'text-sm mb-2'}>Total Score</h3>
                      </div>
                    </div>
                    <div className="flex justify-center">
                      <div className="p-0.5 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500">
                        <div className="rounded-md px-3.5 py-1.5 bg-white font-medium text-black">
                          Grade: <span className="">{creativeAnalysis.letterGrade}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-14">
                    <div className="p-6">
                      <div>
                        <div className="grid lg:grid-cols-2 gap-4">
                          <ScoreCategory
                            title="Hook & First Impressions"
                            strengths={creativeAnalysis.individualFields.first_impression.strengths}
                            optimizations={creativeAnalysis.individualFields.first_impression.recommendations}
                            score={creativeAnalysis.individualFields.first_impression.score}
                          />
                          <ScoreCategory
                            title="Messaging & Copy"
                            strengths={creativeAnalysis.individualFields.messaging_and_copy.strengths}
                            optimizations={creativeAnalysis.individualFields.messaging_and_copy.recommendations}
                            score={creativeAnalysis.individualFields.messaging_and_copy.score}
                          />
                          <ScoreCategory
                            title="Value Proposition"
                            strengths={creativeAnalysis.individualFields.value_proposition.strengths}
                            optimizations={creativeAnalysis.individualFields.value_proposition.recommendations}
                            score={creativeAnalysis.individualFields.value_proposition.score}
                          />
                          <ScoreCategory
                            title="Visual Hierarchy & Design"
                            strengths={creativeAnalysis.individualFields.visual_hierarchy.strengths}
                            optimizations={creativeAnalysis.individualFields.visual_hierarchy.recommendations}
                            score={creativeAnalysis.individualFields.visual_hierarchy.score}
                          />
                          {creativeAnalysis.individualFields.narrative_flow ? (
                            <ScoreCategory
                              title="Narrative Flow (for videos)"
                              strengths={creativeAnalysis.individualFields.narrative_flow?.strengths || []}
                              optimizations={creativeAnalysis.individualFields.narrative_flow?.recommendations || []}
                              score={creativeAnalysis.individualFields.narrative_flow?.score || 0}
                            />
                          ) : null}
                          <ScoreCategory
                            title="Psychological Triggers & Emotion"
                            strengths={creativeAnalysis.individualFields.psychological_triggers.strengths}
                            optimizations={creativeAnalysis.individualFields.psychological_triggers.recommendations}
                            score={creativeAnalysis.individualFields.psychological_triggers.score}
                          />
                          <ScoreCategory
                            title="Audience Fit & Funnel Stage Alignment"
                            strengths={creativeAnalysis.individualFields.audience_fit.strengths}
                            optimizations={creativeAnalysis.individualFields.audience_fit.recommendations}
                            score={creativeAnalysis.individualFields.audience_fit.score}
                          />
                          <ScoreCategory
                            title="Performance Prediction"
                            strengths={creativeAnalysis.individualFields.performance_prediction.strengths}
                            optimizations={creativeAnalysis.individualFields.performance_prediction.recommendations}
                            score={creativeAnalysis.individualFields.performance_prediction.score}
                          />
                        </div>
                        <div className="grid lg:grid-cols-2 gap-4 mt-8">
                          <OverallStrengthOptimizations
                            title="Overall Strengths"
                            list={creativeAnalysis.strengths}
                            type="strength"
                          />
                          <OverallStrengthOptimizations
                            title="Recommended Optimizations"
                            list={creativeAnalysis.recommendations}
                            type="optimization"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </Form>
        </div>
      </Stack>
    </AccessControl>
  )
}

const ScoreCategory = ({
  title,
  strengths,
  optimizations,
  score,
}: { title: string; strengths: string[]; optimizations: string[]; score: number }) => {
  return (
    <div className="border rounded-lg">
      <div className="bg-slate-50 px-6 py-5 flex justify-between items-center rounded-t-lg">
        <h3 className="font-semibold text-sm">{title}</h3>
        <div>
          <div className="rounded border px-2 py-1 bg-white font-medium text-sm">
            {score < 10 ? `0${score}` : score}
            <span className="text-thememutedforeground">/10</span>
          </div>
        </div>
      </div>
      <div className="px-6 py-5 space-y-4">
        <div className="">
          <div className="flex items-end gap-2">
            <span className="text-[#22C55D] text-lg">&#8226;</span>
            <h5 className="font-medium text-sm">Strengths:</h5>
          </div>
          <ul className="list-inside list-disc pl-4 text-slate-500 text-sm">
            {strengths.map((strength) => (
              <li key={strength}>{strength}</li>
            ))}
          </ul>
        </div>

        <div className="">
          <div className="flex items-end gap-2">
            <span className="text-[#F97315] text-lg">&#8226;</span>
            <h5 className="font-medium text-sm">Optimizations:</h5>
          </div>
          <ul className="list-inside list-disc pl-4 text-slate-500 text-sm">
            {optimizations.map((optimization) => (
              <li key={optimization}>{optimization}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

const OverallStrengthOptimizations = ({
  title,
  list,
  type,
}: { title: string; list: string[]; type: 'strength' | 'optimization' }) => {
  return (
    <div className="border rounded-lg">
      <div
        className={`${type === 'strength' ? 'bg-green-100' : 'bg-yellow-100'} px-6 py-5 flex justify-between items-center rounded-t-lg`}
      >
        <h3 className="font-semibold text-base">{title}</h3>
      </div>
      <div className="px-6 py-5 space-y-4">
        <div className="">
          <ul className="list-inside list-disc pl-4 text-slate-500 text-sm">
            {list.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}
